import Vue from "vue";
import Router from "vue-router";
import Routes from "@/routes/routes";
import store from "@/stores/index.js";
import PublicPages from "@/routes/public-pages";
import InactivePages from "@/routes/inactive-pages";
import UnderReviewPages from "@/routes/under-review-pages";
import FeatureLockedPages from "@/routes/feature-locked-pages";
import FeatureIds from '@/stores/features'
import {api} from "@/scripts/api"
import {helpers} from "@/scripts/helpers"
import { ThirdPartyServices } from '@/scripts/services/third-party/third-party-services';
import { LogLevel } from "@/scripts/logger";
import EventBus from '@/scripts/event-bus';
import { inactiveGuard } from '@/guards/inactive-guard';


var routerHelpers = {
    parentPages: null,

    isActivationRequired(inactivePages, path) {
        var isActivationRequired = false;

        if (store.getters.isClientUser) {
            if(!store.state.context.client.isActive) {
                if(!inactivePages.includes(path)) {
                    isActivationRequired = true;
                }
            }
        }

        return isActivationRequired;
    },

    getQueryParamFromHash(to) {
        let tpQueryParam = to.query.tp;
        
        if (!tpQueryParam) {
            const hash = window.location.hash;
            const queryStartIndex = hash.indexOf('?');
            if (queryStartIndex !== -1) {
                const hashParams = new URLSearchParams(hash.substring(queryStartIndex));
                tpQueryParam = hashParams.get('tp');
            }
        }
        
        return tpQueryParam;
    },

    authNext: async function(to, from, next) {
        const token = localStorage.getItem("jwt");
        const tpQueryParam = routerHelpers.getQueryParamFromHash(to);
        
        if (tpQueryParam && !token) {
            localStorage.setItem('urlState', JSON.stringify({
                to
            }));

            const identityProviders = {
                'connectwise': 'connectwise',
            }

            if (!identityProviders[tpQueryParam]) {
                router.push('/Account/Login');
            }
            const identityProvider = identityProviders[tpQueryParam];

            try {
                const response = await ThirdPartyServices.getAuthorizeUrl(identityProvider);
                const { data } = response || {};
                if (Object.keys(data).length && data.data) {
                    const apiData = data.data || {};
                    window.open(apiData.authURL, '_self');
                } else {
                    throw new Error('Something went wrong. Please try again.');
                }
            } catch (error) {
                router.push('/Account/Login');
            }
        }

        if (!token && routerHelpers.isValidSSOCallbackRoute(to)) {
            return next();
        }

        if (to.path.includes('PartnerProgram')) {
            if (token) {
                return next('/');
            }
            else {
                return next();
            }
        }
        
        if (token && routerHelpers.isValidSSOCallbackRoute(to)) {
            return next();
        }

        if (!token) {
            // Attempted to go to a URL without a login token, redirect there after login
            store.commit('setLoginDestinationRoute', to.fullPath);
            return next("/Account/Login");
        }

        if (!store.state.user.id) {
            store.dispatch("hydrateUser")
                .then(() => {
                    routerHelpers.processLastCheckIn();
                    routerHelpers.route(to, from, next);    
                })
                .catch(() => {
                    store
                        .dispatch("logout")
                        .then(() => {
                            router.push("/Account/Login");
                        });
                });
        }
        else {
            routerHelpers.processLastCheckIn();
            routerHelpers.route(to, from, next);
        }
    },

    route(to, from, next) {
        const inactivePages = InactivePages;

        if (routerHelpers.isActivationRequired(inactivePages, to.path)) {
            return next("/Account/Reactivate");
        }
        else {
            routerHelpers
                .setParentContext(to, from)
                .then(() => {
                    store.dispatch('updateIntercom');
                    return next();
                })
                .catch(() => {
                    return next({
                        path: '/404',
                        query: {
                           url: to.fullPath,
                        }
                    });
                });
        }
    },

    isValidSSOCallbackRoute(to) {
        const ssoCallbackUrls = {
            connectwise: '/third-party/connectwise/callback',
            connectwiseDisclaimer: '/third-party/connectwisedisclaimer',
            connectwiseLogin: '/third-party/connectwiselogin',
            connectwiseRegister: '/third-party/connectwiseregister',
            connectwiseSuccess: '/third-party/connectwisesuccesslink',
        };

        return Object.values(ssoCallbackUrls).includes(to.path.toLowerCase());
    },

    isAuthRequired(path) {
        const publicPages = PublicPages;
        const authRequired = !publicPages.includes(path.toLowerCase());

        return authRequired;
    },

    isUnderReview(path) {
        return UnderReviewPages.includes(path.toLowerCase());
    },

    isFeatureLockedRoute(route) {
        var pages = [];
        FeatureLockedPages.forEach(pageType => {
            pageType.pages.forEach(page => {
                pages.push(page.route);
            })
        });
        return pages.includes(route);
    },

    processLastCheckIn() {
        var lastCheckIn = store.getters.lastCheckIn;
        var currentTime = new Date();
        var differenceInMinutes = Math.floor((currentTime - lastCheckIn) / 60000);

        if (differenceInMinutes > 30) {
            store.dispatch('hydrateUser')
                .then(() => {
                    if (store.getters.isClientUser) {
                        if(store.state.context.client.isActive) {
                            store.state.user.lastCheckIn = new Date();
                        }
                        else {
                            router.push("/Account/Reactivate");
                        }
                    }
                    else {
                        store.state.user.lastCheckIn = new Date();
                    }
                })
                .catch(() => {
                    store
                        .dispatch("logout")
                        .then(() => {
                            router.push("/Account/Login");
                        });
                });
        }
    },

    setParentContext(to, from) {
        return new Promise((resolve, reject) => {
            if(store.getters.isAgencyUser) {
                routerHelpers.parentPages = ['/','/Clients', '/Account/Users', '/Account/Company', '/Campaigns/Templates', '/MDF/Management', '/Reports/PartnerResources/SocialMedia'];
    
                routerHelpers
                    .setAgencyContext(to, from)
                    .then(resolve)
                    .catch(reject);
            }
            else if(store.getters.isMasterAgencyUser) {
                routerHelpers.parentPages = ['/','/Clients', '/Account/Users'];
    
                routerHelpers
                    .setMasterAgencyContext(to, from)
                    .then(resolve)
                    .catch(reject);
            }
            else if(store.getters.isOrganizationUser) {
                routerHelpers.parentPages = ['/', '/Clients', '/Account/Users', '/Adoption', '/Applications']

                routerHelpers
                    .setOrganizationContext(to, from)
                    .then(resolve)
                    .catch(reject);
            }
            else if (store.getters.isClientUser) {
                routerHelpers.parentPages = null;

                routerHelpers
                    .setClientContext(to, from)
                    .then(resolve)
                    .catch(reject);
            }
        })
    },

    setMasterAgencyContext(to, from) {
        return new Promise((resolve, reject) => {
            if(!store.state.context.masterAgency.id) {
                store
                    .dispatch('setMasterAgencyContext', store.state.user.masterAgencyId)
                    .then(() => {
                        routerHelpers
                            .setChildContext(to, from)
                            .then(resolve)
                            .catch(reject);
                    })
                    .catch(reject);
            } else if(!store.state.context.client.id || store.state.context.client.id != to.params.clientId) {
                routerHelpers
                    .setChildContext(to, from)
                    .then(resolve)
                    .catch(reject);
            } else {
                resolve();
            }
        })
    },

    setAgencyContext(to, from) {
        return new Promise((resolve, reject) => {
            if(!store.state.context.agency.id) {
                store
                    .dispatch('setAgencyContextFromRouter', { agencyId: store.state.user.agencyId, to })
                    .then(() => {
                        routerHelpers
                            .setChildContext(to, from)
                            .then(resolve)
                            .catch(reject);
                    })
                    .catch(reject);
            } else if(!store.state.context.client.id || store.state.context.client.id != to.params.clientId) {
                routerHelpers
                    .setChildContext(to, from)
                    .then(resolve)
                    .catch(reject);
            } else {
                resolve();
            }
        })
    },

    setOrganizationContext(to, from) {
        return new Promise((resolve, reject) => {
            if(store.state.user.organizationId) {
                routerHelpers
                    .setChildContext(to, from)
                    .then(resolve)
                    .catch(reject);                        
            }
            else {
                resolve();
            }
        })
    },

    setClientContext(to, from) {
        return new Promise((resolve, reject) => {
            if (!store.state.context.client.id) {
                store.dispatch('setClientContext', store.state.user.clientId)
                    .then(resolve)
                    .catch(reject);              
            } else {
                resolve();
            }
        });
    },

    setChildContext(to, from) {
        return new Promise((resolve, reject) => {
            if (to.path.includes('/Clients/') || to.meta.type === 'client') {
                if (routerHelpers.parentPages.includes(from.path)) {
                    if (store.getters.isOrganizationUser || store.getters.isVendorUser || store.getters.isAdmin || store.getters.isMarketer) {
                        localStorage.lastActiveMainNavItem = 'Marketing';
                    } else if (store.getters.isSalesperson) {
                        localStorage.lastActiveMainNavItem = 'Sales';
                    } else {
                        localStorage.lastActiveMainNavItem = 'Marketing';
                    }
                }
            }

            routerHelpers
                .setByContextId(to)
                .then(resolve)
                .catch(reject);
        })
    },

    setByContextId(to) {
        return new Promise((resolve, reject) => {
            if(to.params.clientId) {
                store.dispatch('setClientContext', to.params.clientId).then(resolve).catch(reject);
            }
            else if(to.params.masterAgencyId) {
                store.dispatch('setMasterAgencyContext', to.params.masterAgencyId).then(resolve).catch(reject);
            } 
            else if(to.params.agencyId) {
                store.dispatch('setAgencyContextFromRouter', { agencyId: to.params.agencyId, to }).then(resolve).catch(reject);
            } 
            else if(to.params.contactId) {
                store.dispatch('setClientContextByContactId', to.params.contactId).then(resolve).catch(reject);
            } 
            else if(to.params.templateId) {
                store.dispatch('setContextByTemplateId', to.params.templateId).then(resolve).catch(reject);
            } 
            else if(to.params.journeyId) {
                store.dispatch('setContextByJourneyId', to.params.journeyId).then(resolve).catch(reject);
            } 
            else if(to.params.journeyTemplateId) {
                store.dispatch('setContextByJourneyTemplateId', to.params.journeyTemplateId).then(resolve).catch(reject);
            } 
            else if(to.params.collateralBuildId) {
                store.dispatch('setContextByCollateralBuildId', to.params.collateralBuildId).then(resolve).catch(reject);
            } 
            else if(to.params.campaignId) {
                store.dispatch('setClientContextByCampaignId', to.params.campaignId).then(resolve).catch(reject);
            }
            else if(to.params.landingPageId) {
                store.dispatch('setClientContextByLandingPageId', to.params.landingPageId).then(resolve).catch(reject);
            }
            else if(to.params.campaignTemplateId) {
                store.dispatch('setClientContextByCampaignTemplateId', to.params.campaignTemplateId).then(resolve).catch(reject);
            } 
            else if(to.params.companyId) {
                store.dispatch('setClientContextByCompanyId', to.params.companyId).then(resolve).catch(reject);
            }
            else if(to.params.salesPersonId) {
                store.dispatch('setClientContextBySalespersonId', to.params.salesPersonId).then(resolve).catch(reject);
            }
            else if(to.params.listId) {
                store.dispatch('setClientContextByListId', to.params.listId).then(resolve).catch(reject);
            }
            else if(to.params.marketingPlanId) {
                store.dispatch('setContextByMarketingPlanId', to.params.marketingPlanId).then(resolve).catch(reject);
            } 
            else if(to.params.marketingPlanTemplateId) {
                store.dispatch('setContextByMarketingPlanTemplateId', to.params.marketingPlanTemplateId).then(resolve).catch(reject);
            } 
            else if (to.params.userId) {
                store.dispatch('setContextByUserId', to.params.userId).then(resolve).catch(reject);
            }
            else if (store.state.user.clientId) {
                store.dispatch('setClientContext', store.state.user.clientId).then(resolve).catch(reject);
            }
            else if (store.state.user.agencyId) {
                store.dispatch('setAgencyContextFromRouter', { agencyId: store.state.user.agencyId, to }).then(resolve).catch(reject);
            }
            else if (store.state.user.masterAgencyId) {
                store.dispatch('setMasterAgencyContext', store.state.user.masterAgencyId).then(resolve).catch(reject);
            }
            else {
                resolve();
            }
        });
    },

    applyFeatureLock(to) {
        if (store.state.context.client.id) {
            var pageName = to.matched[0].name;      
    
            var routeBaseUrl = store.getters.routeBaseUrl;
    
            var collateralBuilderPages = FeatureLockedPages.find(feature => feature.pageType === 'Collateral Build').pages.map(page => {
                return page.name
            });
            var marketingPlanPages = FeatureLockedPages.find(feature => feature.pageType === 'Marketing Plan').pages.map(page => {
                return page.name
            });
            var journeyPages = FeatureLockedPages.find(feature => feature.pageType === 'Journey').pages.map(page => {
                return page.name
            });
    
            var enabledClientFeatures = store.state.context.client.enabledFeatures;
            var isCollateralBuilderPage = collateralBuilderPages.includes(pageName);
            var collateralBuilderEnabled = enabledClientFeatures.some(f => f.id === FeatureIds.collateralBuilder);
            
            var isJourneyPage = journeyPages.includes(pageName);
            var isMarketingPlanPage = marketingPlanPages.includes(pageName);
            var automationEnabled = enabledClientFeatures.some(f => f.id === FeatureIds.automation);
    
            if (isCollateralBuilderPage && !collateralBuilderEnabled) {
                router.push(`${ routeBaseUrl }/Account/Upgrade/CollateralBuilds`);
            }
            else if (isMarketingPlanPage && !automationEnabled) {
                router.push(`${ routeBaseUrl }/Account/Upgrade/MarketingPlans`);
            }
            else if (isJourneyPage && !automationEnabled) {
                router.push(`${ routeBaseUrl }/Account/Upgrade/Journeys`);
            }
        }
    }
}

Vue.use(Router);

export const router = new Router({
    mode: "history",
    base: process.env.BASE_URL,
    routes: Routes,
    scrollBehavior (to, from, savedPosition) {
        return { x: 0, y: 0 };
    }
});
router.beforeEach((to, from, next) => {
    if(!inactiveGuard(to, from, next)) {
        return;
    }

    const clientId = to.params?.clientId;
    const canManageSession = (to.path.includes('/Clients/') || to.meta.type === 'client') && !to.path.includes('/PrivacyEnabled/') && clientId;
    let accountId;

    if (canManageSession) {
        api.get(`/api/Clients/${clientId}/Account`).then((res) => {
            accountId = res.data.Id;
            return api.get(`/ms/account-privacy/v1/accountprivacy/status/${accountId}`);
        }).then((status) => {
            const isEnable = status.data.data.isEnable;
            const hasSession = Number(sessionStorage.getItem('privacy_session_id'));
            const intervalId = Number(sessionStorage.getItem('privacy_interval_id'));
            if (!isEnable) {
                return;
            }
            if (!hasSession) {
                next(`/Clients/${clientId}/PrivacyEnabled/${accountId}`);
                return;
            }
            const lastCheckInterval = sessionStorage.getItem('privacy_last_check_date');
            const currentInterval = Date.now();
            const diff = Math.ceil((currentInterval - lastCheckInterval) / (1000 * 60));
            EventBus.$emit('privacy-session-started', intervalId ? intervalId : 1)
            if (diff >= 60) {
                sessionStorage.removeItem('privacy_session_id');
                sessionStorage.removeItem('privacy_interval_id');
                sessionStorage.removeItem('privacy_last_check_date');
                next('/');
                clearInterval(intervalId);
            }
        })
    }

    if (routerHelpers.isAuthRequired(to.matched[0].path)) {
        if (routerHelpers.isUnderReview(to.matched[0].path) && store.state.context.client.underReview) {
            helpers.showFailNotification('Unable to perform this action while your account is under review.', { level: LogLevel.INFO });
        }
        else {
            routerHelpers.authNext(to, from, next);
        }
    }
    else {
        next();
    }
});

router.beforeEach(async(to, from, next) => {
    const accountId = store.state.context.account.id
    const payload = {
        accountId
    }

    const url = '/api/usage'
    if (accountId){
        api.post(url, payload)
        .catch((error) => {
            helpers.showFailNotification('An error occurred while logging this session', { error, enable: false });
        })
    }
    next()        
})

router.afterEach((to, from, next) => {
    Vue.nextTick(() => {
        if (to.meta.pageTitle) {
            document.title = to.meta.pageTitle + ' - GlassHive';
        }
        else {
            document.title = 'GlassHive';
        }
    });

    if (routerHelpers.isUnderReview(to.matched[0].path) && store.state.context.client.underReview) {
        if (store.getters.isAgencyUser) {
            var clientId = store.state.context.client.id;
            router.push(`/Clients/${ clientId }/Marketing`);
        } else if (store.getters.isClientUser) {
            router.push(`/Marketing`);
        }
        helpers.showFailNotification('Unable to perform this action while your account is under review.', { level: LogLevel.INFO });
    }

    if (store.getters.isParentUser) {
        if (to.params.masterAgencyId) {
            store.commit('clearAgencyContext');
            store.commit('clearClientContext');
        }
        else if (to.params.agencyId) {
            store.commit('clearClientContext');
            store.commit('clearMasterAgencyContext');
        }
        else if (to.params.clientId) {
            store.commit('clearAgencyContext');
            store.commit('clearMasterAgencyContext');
        }
    }

    if (store.getters.isParentUser && routerHelpers.parentPages.includes(to.path)) {
        store.commit('clearClientContext');
        
        if (store.getters.isOrganizationUser) {
            store.commit('clearAgencyContext');
            store.commit('clearMasterAgencyContext');
            store.commit('clearAccountContext');
        } 
        else if (store.getters.isMasterAgencyUser) {
            store.commit('clearAgencyContext');
        }
    }

    if (routerHelpers.isFeatureLockedRoute(to.matched[0].path)){
        routerHelpers.applyFeatureLock(to);
    };
});
